/** The API reference, rendered from the contract (packages/platform/src/generated/public-api.json). */
import { Link, useParams } from 'react-router';
import { publicApi, type ApiField, type ApiOperation, type ApiReference } from '@p1plug/platform';
import { ApiPath, DataTable, EmptyState, Tag } from '@p1plug/ui';
import { C, DocPage, P, Snippet } from '../doc';

const api = publicApi as ApiReference;

export const groupSlug = (group: string) => group.toLowerCase().replace(/\s+/g, '-');

const GROUP_LEADS: Record<string, string> = {
  Authentication: 'Exchange a machine client’s credentials for an access token.',
  Account: 'Who the credential belongs to and what it may do.',
  Orders: 'Ask P1Plug to equip a customer, and follow the order to activation.',
  'Meter data': 'Read a meter point: its customer, its device and its telemetry.',
  Devices: 'Installations and the physical devices behind them, for support.',
  Events: 'Read your event log at your own pace, and replay it after an outage.',
  Webhooks: 'Have events pushed to your endpoint, and inspect deliveries.',
  'Data sharing': 'Record a consumer’s consent to share their data with another organisation.',
};

function curl(op: ApiOperation): string {
  const path = op.path.replace(/\{(\w+)\}/g, (_, name: string) => `$${name.toUpperCase()}`);
  if (op.path === '/oauth/token')
    return `curl -X POST "$P1PLUG_API_URL${path}" \\\n  -u "$P1PLUG_CLIENT_ID:$P1PLUG_CLIENT_SECRET" \\\n  -d grant_type=client_credentials`;
  const auth = op.group === 'Authentication' ? '' : ` \\\n  -H "Authorization: Bearer $P1PLUG_TOKEN"`;
  const body = op.requestBody ? ` \\\n  -H "Content-Type: application/json" \\\n  -d @request.json` : '';
  return `curl${op.method === 'GET' ? '' : ` -X ${op.method}`} "$P1PLUG_API_URL${path}"${auth}${body}`;
}

function Fields({ fields }: { fields: ApiField[] }) {
  return (
    <DataTable
      rowKey={(f) => f.name}
      columns={[
        {
          key: 'name',
          label: 'Field',
          mono: true,
          render: (f) => (
            <>
              {f.name} {f.required ? <Tag>required</Tag> : null}
            </>
          ),
        },
        { key: 'type', label: 'Type', mono: true },
        { key: 'description', label: 'Description', render: (f) => f.description ?? '' },
      ]}
      rows={fields}
    />
  );
}

function Operation({ op }: { op: ApiOperation }) {
  return (
    <section className="dev-op" id={op.id}>
      <h2 className="dev-op-title">{op.summary}</h2>
      <p className="dev-op-path">
        <ApiPath method={op.method} path={op.path} />
        {op.scopes.map((s) => (
          <span key={s} className="dev-op-scope">
            scope <C>{s}</C>
          </span>
        ))}
      </p>
      {op.description ? <P>{op.description}</P> : null}
      <div className="dev-op-grid">
        <div className="p1x-stack">
          {op.parameters.length > 0 ? (
            <div>
              <h3 className="p1x-sub">Parameters</h3>
              <DataTable
                rowKey={(p) => p.name}
                columns={[
                  {
                    key: 'name',
                    label: 'Name',
                    mono: true,
                    render: (p) => (
                      <>
                        {p.name} {p.required ? <Tag>required</Tag> : null}
                      </>
                    ),
                  },
                  { key: 'in', label: 'In' },
                  { key: 'type', label: 'Type', mono: true },
                  { key: 'description', label: 'Description', render: (p) => p.description ?? '' },
                ]}
                rows={op.parameters}
              />
            </div>
          ) : null}
          {op.requestBody && op.requestBody.fields.length > 0 ? (
            <div>
              <h3 className="p1x-sub">
                Request body
                {op.requestBody.schema ? (
                  <>
                    {' '}
                    · <C>{op.requestBody.schema}</C>
                  </>
                ) : null}
              </h3>
              <Fields fields={op.requestBody.fields} />
            </div>
          ) : null}
          <div>
            <h3 className="p1x-sub">Responses</h3>
            <DataTable
              rowKey={(r) => r.status}
              columns={[
                { key: 'status', label: 'Status', mono: true },
                { key: 'description', label: 'Meaning' },
                {
                  key: 'schema',
                  label: 'Body',
                  mono: true,
                  render: (r) =>
                    r.schema && api.schemas[r.schema] ? (
                      <a href={`#schema-${r.schema}`}>{r.schema}</a>
                    ) : (
                      (r.schema ?? '')
                    ),
                },
              ]}
              rows={op.responses}
            />
          </div>
        </div>
        <Snippet title="cURL" code={curl(op)} />
      </div>
    </section>
  );
}

export function Reference() {
  const groups = [...new Set(api.operations.map((o) => o.group))];
  return (
    <DocPage
      title="API reference"
      lead={`${api.title} ${api.version}. ${api.operations.length} operations under /api/v1, generated from the API contract, so this page and the API cannot disagree.`}
    >
      <ul className="p1x-prose dev-conventions">
        <li>JSON in and out; UTF-8; times in RFC 3339 UTC.</li>
        <li>Integers for measurements: watts, watt-hours, tenths of a volt, milliamperes.</li>
        <li>
          <C>Authorization: Bearer …</C> on every request: an API key or an OAuth access token.
        </li>
        <li>
          Errors share one format; see <Link to="/docs/errors">Errors</Link>.
        </li>
      </ul>
      {groups.map((g) => (
        <section key={g} className="dev-group">
          <h2 className="p1x-section-title">
            <Link to={`/reference/${groupSlug(g)}`}>{g}</Link>
          </h2>
          <p className="p1x-muted">{GROUP_LEADS[g]}</p>
          <ul className="dev-oplist" role="list">
            {api.operations
              .filter((o) => o.group === g)
              .map((o) => (
                <li key={o.id}>
                  <Link to={`/reference/${groupSlug(g)}#${o.id}`}>
                    <ApiPath method={o.method} path={o.path} />
                  </Link>
                  <span>{o.summary}</span>
                </li>
              ))}
          </ul>
        </section>
      ))}
    </DocPage>
  );
}

export function ReferenceGroup() {
  const { group = '' } = useParams();
  const name = [...new Set(api.operations.map((o) => o.group))].find((g) => groupSlug(g) === group);
  if (!name)
    return (
      <EmptyState
        title="No such section in the API reference"
        action={{ label: 'All operations', to: '/reference', variant: 'secondary' }}
      />
    );
  const ops = api.operations.filter((o) => o.group === name);
  const used = [...new Set(ops.flatMap((o) => [o.requestBody?.schema, ...o.responses.map((r) => r.schema)]))].filter(
    (s): s is string => Boolean(s && api.schemas[s]),
  );
  return (
    <DocPage
      title={name}
      lead={GROUP_LEADS[name] ?? ''}
      crumbs={[{ label: 'API reference', to: '/reference' }, { label: name }]}
    >
      {ops.map((op) => (
        <Operation key={op.id} op={op} />
      ))}
      {used.length > 0 ? (
        <section className="dev-op">
          <h2 className="dev-op-title">Objects</h2>
          {used.map((s) => (
            <div key={s} id={`schema-${s}`} className="dev-schema">
              <h3 className="p1x-sub">
                <code>{s}</code>
              </h3>
              <Fields fields={api.schemas[s]!} />
            </div>
          ))}
        </section>
      ) : null}
    </DocPage>
  );
}
